import { MainContentLayout } from '@wonderschool/common-base-ui';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../../../Components/Shared/PageHeader';
import PageTitle from '../../../../Components/Shared/PageTitle';
import { WsSpinner, WsWidgetSizeEnum } from '../../../../common';
import { EmptyReport } from '../../../../reports';
import './FinancialSummary.scss';
import FinancialSummaryFiltersForm from './FinancialSummaryFiltersForm';
import FinancialSummaryOverview from './FinancialSummaryOverview';
import FinancialSummaryReport from './FinancialSummaryReport';
import { MONTH_LIST } from './constants';
import { useFinancialSummary } from './financialSummaryHooks';
import { FinancialSummaryFiltersType } from './types';

const FinancialSummaryContainer: React.FC = () => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<FinancialSummaryFiltersType>();

  const { financialSummary, isLoading } = useFinancialSummary(filters) || { financialSummary: [], isLoading: false };

  const onFilter = useCallback((filters: FinancialSummaryFiltersType) => {
    setFilters(filters);
  }, []);

  const emptyTitle = useMemo<string | undefined>(() => {
    const translatedMonthList = filters?.selectedMonths?.map((monthKey) => t(monthKey)) || [];
    const reportRange = translatedMonthList.join(', ') + ' ' + filters?.selectedYear?.toString();
    return filters
      ? t('financialSummary.report.nodata.error', { range: reportRange })
      : t('financialSummary.report.required.error');
  }, [filters, t]);

  const selectedYear = filters?.selectedYear;
  const selectedMonths = [...(filters?.selectedMonths || [])].sort((a, b) => {
    return MONTH_LIST.indexOf(a) - MONTH_LIST.indexOf(b);
  });
  // Remove undefined values from the array
  const reportColumns = [selectedYear, ...selectedMonths, 'financialSummary.report.title.total'].filter(
    Boolean
  ) as string[];

  return (
    <>
      {isLoading && <WsSpinner hasOverlay={true} spinnerColor="sky" size={WsWidgetSizeEnum.LARGE} />}
      <MainContentLayout data-testid={'financial-summary-container'}>
        <PageHeader pageName={'financialSummary.title'} classes="attendance" />
        <PageTitle title={t('financialSummary.title')} />
        <FinancialSummaryFiltersForm onFilter={onFilter} loading={isLoading} />
        <FinancialSummaryOverview loading={isLoading} financialSummary={financialSummary} filters={filters} />
        <div className="h-full w-full">
          {isLoading ? null : financialSummary?.length === 0 ? (
            <EmptyReport title={emptyTitle} img={filters ? 'images/empty-data-report.svg' : ''} />
          ) : (
            <>
              <div className="padding top">
                <FinancialSummaryReport data={financialSummary} columns={reportColumns} />
              </div>
            </>
          )}
        </div>
      </MainContentLayout>
    </>
  );
};
export default FinancialSummaryContainer;
